import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Bus, ArrowLeft, User, MapPin, Clock, Star, 
  CheckCircle, Info, ChevronRight, Luggage, Navigation
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusSeatSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trip, passengers: initialPassengers } = location.state || {};
  
  // Maximum seats that can be selected (for group bookings)
  const MAX_SEATS = 10;
  
  const [loading, setLoading] = useState(true);
  const [seatMap, setSeatMap] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengerDetails, setPassengerDetails] = useState([]);
  const [step, setStep] = useState(1); // 1: seat selection, 2: passenger details
  const [extraLuggage, setExtraLuggage] = useState(0);
  
  // Destination selection state
  const [destinations, setDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [loadingDestinations, setLoadingDestinations] = useState(false);

  // Platform processing fee config (per-seat, replaces legacy flat service fee)
  const [platformFeeConfig, setPlatformFeeConfig] = useState({ perSeat: 10, enabled: true, label: 'Processing fee' });

  useEffect(() => {
    if (!trip) {
      navigate('/bus');
      return;
    }
    fetchDestinations();
    (async () => {
      try {
        const { data } = await api.get('/bus/settings');
        if (data?.settings?.platformFee) setPlatformFeeConfig(data.settings.platformFee);
      } catch (e) { /* keep defaults */ }
    })();
  }, [trip]);

  // Refetch seat map when destination changes
  useEffect(() => {
    if (trip && selectedDestination) {
      fetchSeatMap();
    }
  }, [selectedDestination]);

  useEffect(() => {
    // Initialize passenger details when seats are selected
    if (selectedSeats.length > 0 && passengerDetails.length !== selectedSeats.length) {
      setPassengerDetails(selectedSeats.map((seat, idx) => ({
        name: '',
        phone: '',
        id_number: '',
        seat: seat.seatId,
        seat_class: seat.seatClass
      })));
    }
  }, [selectedSeats]);

  const fetchSeatMap = async () => {
    try {
      // Pass boarding and alighting cities for segment-aware availability
      const params = new URLSearchParams();
      if (trip?.route?.origin?.city) {
        params.append('boarding_city', trip.route.origin.city);
      }
      if (selectedDestination?.city) {
        params.append('alighting_city', selectedDestination.city);
      }
      
      const response = await api.get(`/bus/trips/${trip.tripId}/seats?${params.toString()}`);
      setSeatMap(response.data.seatMap);
      
      // Clear selected seats if they're no longer available for this segment
      if (response.data.seatMap?.seats) {
        const availableSeats = response.data.seatMap.seats
          .filter(s => s.status === 'available')
          .map(s => s.seatId);
        setSelectedSeats(prev => prev.filter(s => availableSeats.includes(s.seatId)));
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load seat map',
        variant: 'destructive'
      });
      navigate('/bus');
    } finally {
      setLoading(false);
    }
  };

  const fetchDestinations = async () => {
    setLoadingDestinations(true);
    try {
      const response = await api.get(`/bus/trips/${trip.tripId}/destinations`);
      const destinationList = response.data.destinations || [];
      setDestinations(destinationList);
      
      // Default to final destination
      const finalDest = destinationList.find(d => d.isFinalDestination);
      if (finalDest) {
        setSelectedDestination(finalDest);
      }
    } catch (error) {
      console.error('Failed to fetch destinations:', error);
      // Fallback to trip route destination
      setDestinations([{
        city: trip?.route?.destination?.city,
        station: trip?.route?.destination?.station,
        isFinalDestination: true,
        pricing: trip?.pricing
      }]);
      setSelectedDestination({
        city: trip?.route?.destination?.city,
        station: trip?.route?.destination?.station,
        isFinalDestination: true,
        pricing: trip?.pricing
      });
    } finally {
      setLoadingDestinations(false);
    }
  };

  const handleSeatClick = (seat) => {
    if (seat.status !== 'available') return;

    const isSelected = selectedSeats.find(s => s.seatId === seat.seatId);
    
    if (isSelected) {
      // Deselect the seat
      setSelectedSeats(selectedSeats.filter(s => s.seatId !== seat.seatId));
    } else {
      // Check if max seats limit reached
      if (selectedSeats.length >= MAX_SEATS) {
        toast({
          title: 'Maximum Seats Reached',
          description: `You can select up to ${MAX_SEATS} seats per booking`,
        });
        return;
      }
      // Add seat to selection
      setSelectedSeats([...selectedSeats, seat]);
    }
  };

  const getSeatColor = (seat) => {
    const isSelected = selectedSeats.find(s => s.seatId === seat.seatId);
    
    if (isSelected) return 'bg-blue-600 text-white border-blue-700';
    if (seat.status === 'booked') return 'bg-gray-300 text-gray-500 cursor-not-allowed';
    if (seat.status === 'blocked') return 'bg-red-200 text-red-500 cursor-not-allowed';
    
    switch (seat.seatClass) {
      case 'vip': return 'bg-yellow-100 border-yellow-400 hover:bg-yellow-200';
      case 'business': return 'bg-purple-100 border-purple-400 hover:bg-purple-200';
      default: return 'bg-white border-gray-300 hover:bg-blue-50';
    }
  };

  const calculateTotal = () => {
    // Get price based on selected destination
    const getPriceForSeat = (seat) => {
      if (selectedDestination?.pricing) {
        const destPricing = selectedDestination.pricing;
        // Use destination-specific pricing if available
        const classPrice = destPricing[seat.seatClass] || destPricing.standard || seat.price;
        return classPrice;
      }
      return seat.price;
    };
    
    const seatTotal = selectedSeats.reduce((sum, seat) => sum + getPriceForSeat(seat), 0);
    const luggageFee = extraLuggage * (trip.bus?.luggageAllowance?.extra_per_kg || 5);
    const perSeat = platformFeeConfig?.enabled === false ? 0 : (platformFeeConfig?.perSeat ?? 10);
    const platformFee = perSeat * selectedSeats.length;
    return {
      subtotal: Math.round(seatTotal * 100) / 100,
      luggageFee,
      platformFee,
      platformFeePerSeat: perSeat,
      seatCount: selectedSeats.length,
      serviceFee: platformFee, // legacy alias for any downstream readers
      total: Math.round((seatTotal + luggageFee + platformFee) * 100) / 100
    };
  };

  const handleContinue = () => {
    if (selectedSeats.length === 0) {
      toast({
        title: 'Select Seats',
        description: 'Please select at least one seat to continue',
        variant: 'destructive'
      });
      return;
    }
    setStep(2);
  };

  const handlePassengerChange = (index, field, value) => {
    const updated = [...passengerDetails];
    updated[index][field] = value;
    setPassengerDetails(updated);
  };

  const handleProceedToPayment = () => {
    // Validate passenger details
    for (let i = 0; i < passengerDetails.length; i++) {
      if (!passengerDetails[i].name || !passengerDetails[i].phone) {
        toast({
          title: 'Missing Details',
          description: `Please fill in details for Passenger ${i + 1}`,
          variant: 'destructive'
        });
        return;
      }
    }

    navigate('/bus/checkout', {
      state: {
        trip,
        selectedSeats,
        passengerDetails,
        extraLuggage,
        pricing: calculateTotal(),
        selectedDestination // Pass the selected destination
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!seatMap) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Failed to load seat map</p>
      </div>
    );
  }

  const pricing = calculateTotal();
  const layout = seatMap.layout;
  const rows = layout?.rows || 12;
  const seatsPerRow = layout?.seatsPerRow || 4;
  const columns = ['A', 'B', 'C', 'D'].slice(0, seatsPerRow);
  const aisleAfter = layout?.aisleAfterSeat || 2;

  // Group seats by row
  const seatsByRow = {};
  seatMap.seats?.forEach(seat => {
    if (!seatsByRow[seat.row]) seatsByRow[seat.row] = [];
    seatsByRow[seat.row].push(seat);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => step === 1 ? navigate('/bus') : setStep(1)}>
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">
                {step === 1 ? 'Select Your Seats' : 'Passenger Details'}
              </h1>
              <p className="text-blue-100 text-sm">
                {trip?.route?.origin?.city} → {trip?.route?.destination?.city}
              </p>
            </div>
          </div>
          
          {/* Trip Summary */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {trip?.departureTime}
            </div>
            <div className="flex items-center gap-1">
              <Bus className="h-4 w-4" />
              {trip?.operatorName}
            </div>
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4" />
              {trip?.operatorRating?.toFixed(1) || 'New'}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {step === 1 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Seat Map */}
            <div className="lg:col-span-2">
              <Card className="p-4" data-testid="seat-map">
                {/* Legend */}
                <div className="flex flex-wrap gap-4 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-white border border-gray-300" />
                    <span>Standard</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-purple-100 border border-purple-400" />
                    <span>Business</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-yellow-100 border border-yellow-400" />
                    <span>VIP</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-blue-600" />
                    <span>Selected</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-gray-300" />
                    <span>Booked</span>
                  </div>
                </div>

                {/* Bus Front */}
                <div className="text-center mb-4">
                  <div className="inline-block bg-gray-200 px-8 py-2 rounded-t-xl">
                    <span className="text-sm text-gray-600">DRIVER</span>
                  </div>
                </div>

                {/* Seats Grid */}
                <div className="flex flex-col items-center gap-2">
                  {Array.from({ length: rows }, (_, rowIdx) => {
                    const row = rowIdx + 1;
                    const rowSeats = seatsByRow[row] || [];
                    
                    return (
                      <div key={row} className="flex items-center gap-2">
                        <span className="w-6 text-xs text-gray-400 text-right">{row}</span>
                        <div className="flex gap-1">
                          {columns.map((col, colIdx) => {
                            const seat = rowSeats.find(s => s.column === col);
                            const showAisle = colIdx === aisleAfter - 1;
                            
                            return (
                              <React.Fragment key={col}>
                                <button
                                  onClick={() => seat && handleSeatClick(seat)}
                                  disabled={seat?.status !== 'available'}
                                  className={`w-10 h-10 rounded border-2 flex items-center justify-center text-xs font-medium transition-all ${
                                    seat ? getSeatColor(seat) : 'bg-gray-100 border-gray-200'
                                  }`}
                                  data-testid={`seat-${row}${col}`}
                                >
                                  {seat ? `${row}${col}` : ''}
                                </button>
                                {showAisle && <div className="w-6" />}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>

            {/* Selection Summary */}
            <div>
              <Card className="p-4 sticky top-4" data-testid="booking-summary">
                <h3 className="font-semibold mb-4">Your Selection</h3>
                
                {selectedSeats.length === 0 ? (
                  <p className="text-gray-500 text-sm mb-4">
                    Click on available seats to select them (max {MAX_SEATS})
                  </p>
                ) : (
                  <div className="space-y-2 mb-4">
                    {selectedSeats.map((seat, idx) => {
                      // Calculate adjusted price for display
                      const adjustedPrice = selectedDestination?.pricing 
                        ? (selectedDestination.pricing[seat.seatClass] || selectedDestination.pricing.standard || seat.price)
                        : seat.price;
                      return (
                        <div key={seat.seatId} className="flex justify-between items-center p-2 bg-blue-50 rounded">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-blue-600" />
                            <span className="font-medium">Seat {seat.seatId}</span>
                            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">
                              {seat.seatClass}
                            </span>
                          </div>
                          <span className="font-semibold">K{Math.round(adjustedPrice)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Destination Selector */}
                {destinations.length > 1 && (
                  <div className="border-t pt-4 mb-4">
                    <Label className="flex items-center gap-2 mb-2">
                      <Navigation className="h-4 w-4 text-red-500" />
                      Alighting Point
                    </Label>
                    <select
                      value={selectedDestination?.city || ''}
                      onChange={(e) => {
                        const dest = destinations.find(d => d.city === e.target.value);
                        setSelectedDestination(dest);
                      }}
                      className="w-full p-2 border rounded-md bg-white text-sm"
                      data-testid="destination-selector"
                    >
                      {destinations.map((dest, idx) => (
                        <option key={idx} value={dest.city}>
                          {dest.city} {dest.isFinalDestination ? '(Final)' : ''}
                          {dest.pricing?.priceMultiplier && !dest.isFinalDestination 
                            ? ` - ${Math.round(dest.pricing.priceMultiplier * 100)}% of fare`
                            : ''
                          }
                        </option>
                      ))}
                    </select>
                    {selectedDestination && !selectedDestination.isFinalDestination && (
                      <div className="mt-2 p-2 bg-amber-50 rounded-md text-xs text-amber-700">
                        <Info className="h-3 w-3 inline mr-1" />
                        Intermediate stop - fare adjusted to {Math.round((selectedDestination.pricing?.priceMultiplier || 1) * 100)}% of full journey
                      </div>
                    )}
                  </div>
                )}

                {/* Extra Luggage */}
                <div className="border-t pt-4 mb-4">
                  <Label className="flex items-center gap-2 mb-2">
                    <Luggage className="h-4 w-4" />
                    Extra Luggage (kg)
                  </Label>
                  <Input
                    type="number"
                    value={extraLuggage}
                    onChange={(e) => setExtraLuggage(Math.max(0, parseInt(e.target.value) || 0))}
                    min={0}
                    max={50}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {trip.bus?.luggageAllowance?.included_kg || 20}kg included per passenger. 
                    K{trip.bus?.luggageAllowance?.extra_per_kg || 5}/kg extra
                  </p>
                </div>

                {/* Pricing */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>K{pricing.subtotal}</span>
                  </div>
                  {pricing.luggageFee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Luggage Fee</span>
                      <span>K{pricing.luggageFee}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span>{platformFeeConfig?.label || 'Processing fee'} {pricing.seatCount > 1 ? `(K${pricing.platformFeePerSeat} × ${pricing.seatCount})` : ''}</span>
                    <span data-testid="seatselect-platform-fee">K{pricing.platformFee}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total</span>
                    <span className="text-blue-600">K{pricing.total}</span>
                  </div>
                </div>

                <Button 
                  className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                  disabled={selectedSeats.length === 0}
                  onClick={handleContinue}
                  data-testid="continue-btn"
                >
                  Continue with {selectedSeats.length} seat{selectedSeats.length !== 1 ? 's' : ''}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </Card>
            </div>
          </div>
        ) : (
          /* Step 2: Passenger Details */
          <div className="max-w-2xl mx-auto">
            <Card className="p-6" data-testid="passenger-details-form">
              <h2 className="text-xl font-semibold mb-4">Passenger Details</h2>
              
              <div className="space-y-6">
                {passengerDetails.map((passenger, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <User className="h-5 w-5 text-blue-600" />
                      <h3 className="font-medium">Passenger {idx + 1}</h3>
                      <span className="text-sm bg-blue-100 text-blue-600 px-2 py-0.5 rounded ml-auto">
                        Seat {passenger.seat}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Full Name *</Label>
                        <Input
                          value={passenger.name}
                          onChange={(e) => handlePassengerChange(idx, 'name', e.target.value)}
                          placeholder="As on ID"
                          data-testid={`passenger-${idx}-name`}
                        />
                      </div>
                      <div>
                        <Label>Phone Number *</Label>
                        <Input
                          value={passenger.phone}
                          onChange={(e) => handlePassengerChange(idx, 'phone', e.target.value)}
                          placeholder="+260..."
                          data-testid={`passenger-${idx}-phone`}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>ID Number (Optional)</Label>
                        <Input
                          value={passenger.id_number}
                          onChange={(e) => handlePassengerChange(idx, 'id_number', e.target.value)}
                          placeholder="NRC or Passport"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Total Amount</span>
                  <span className="text-2xl font-bold text-blue-600">K{pricing.total}</span>
                </div>
                <p className="text-sm text-gray-500">
                  {selectedSeats.length} seat(s) • {trip?.route?.origin?.city} → {selectedDestination?.city || trip?.route?.destination?.city}
                  {selectedDestination && !selectedDestination.isFinalDestination && (
                    <span className="ml-1 text-amber-600">(Intermediate Stop)</span>
                  )}
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={handleProceedToPayment}
                  data-testid="proceed-payment-btn"
                >
                  Proceed to Payment
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusSeatSelection;
